import type { DocumentModelGlobalState } from "document-model";

export const documentModel: DocumentModelGlobalState = {
  id: "powerhouse/renown-credential",
  name: "RenownCredential",
  author: {
    name: "Powerhouse Inc.",
    website: "https://www.powerhouse.inc",
  },
  extension: "phrc",
  description: "A Renown Credential",
  specifications: [
    {
      state: {
        local: {
          schema: "",
          examples: [],
          initialValue: "",
        },
        global: {
          schema:
            'type Issuer {\n  id: String!\n  ethereumAddress: EthereumAddress!\n}\n\ntype CredentialSubject {\n  id: String\n  app: String!\n}\n\ntype CredentialStatus {\n  id: String!\n  type: String!\n}\n\ntype CredentialSchema {\n  id: String!\n  type: String!\n}\n\ntype EIP712Domain {\n  version: String!\n  chainId: Int!\n}\n\ntype EIP712 {\n  domain: EIP712Domain!\n  primaryType: String!\n}\n\ntype Proof {\n  verificationMethod: String!\n  ethereumAddress: EthereumAddress!\n  created: DateTime!\n  proofPurpose: String!\n  type: String!\n  proofValue: String!\n  eip712: EIP712!\n}\n\ntype RenownCredentialState {\n  "W3C VC Fields - EIP-712 Signed Verifiable Credential"\n  context: [String!]!\n  id: String!\n  type: [String!]!\n  issuer: Issuer!\n  issuanceDate: DateTime!\n  expirationDate: DateTime\n  credentialSubject: CredentialSubject!\n  credentialStatus: CredentialStatus\n  credentialSchema: CredentialSchema!\n  proof: Proof!\n\n  "Revocation tracking"\n  revoked: Boolean!\n  revokedAt: DateTime\n  revocationReason: String\n}\n\n\n\n# manager\n\ninput IssuerInput {\n  id: String!\n  ethereumAddress: EthereumAddress!\n}\n\ninput CredentialSubjectInput {\n  id: String\n  app: String!\n}\n\ninput CredentialStatusInput {\n  id: String!\n  type: String!\n}\n\ninput CredentialSchemaInput {\n  id: String!\n  type: String!\n}\n\ninput EIP712DomainInput {\n  version: String!\n  chainId: Int!\n}\n\ninput EIP712Input {\n  domain: EIP712DomainInput!\n  primaryType: String!\n}\n\ninput ProofInput {\n  verificationMethod: String!\n  ethereumAddress: EthereumAddress!\n  created: DateTime!\n  proofPurpose: String!\n  type: String!\n  proofValue: String!\n  eip712: EIP712Input!\n}\n\ninput InitInput {\n  context: [String!]!\n  id: String!\n  type: [String!]!\n  issuer: IssuerInput!\n  issuanceDate: DateTime!\n  expirationDate: DateTime\n  credentialSubject: CredentialSubjectInput!\n  credentialStatus: CredentialStatusInput\n  credentialSchema: CredentialSchemaInput!\n  proof: ProofInput!\n}\n\ninput RevokeInput {\n  revokedAt: DateTime!\n  reason: String\n}',
          examples: [],
          initialValue:
            '{"context": [], "id": "", "type": [], "issuer": {"id": "", "ethereumAddress": "0x0000000000000000000000000000000000000000"}, "issuanceDate": "", "expirationDate": null, "credentialSubject": {"id": null, "app": ""}, "credentialStatus": null, "credentialSchema": {"id": "", "type": ""}, "proof": {"verificationMethod": "", "ethereumAddress": "0x0000000000000000000000000000000000000000", "created": "", "proofPurpose": "", "type": "", "proofValue": "", "eip712": {"domain": {"version": "", "chainId": 0}, "primaryType": "VerifiableCredential"}}, "revoked": false, "revokedAt": null, "revocationReason": null}',
        },
      },
      modules: [
        {
          id: "ea1ea725-62a9-4e97-8f89-6b153b7af4ee",
          name: "manager",
          description: "",
          operations: [
            {
              id: "366dfd44-377f-42d7-949d-a8d82b6a909d",
              name: "INIT",
              description:
                "Initialize a W3C Verifiable Credential with required fields",
              schema:
                "input InitInput {\n  context: [String!]!\n  id: String!\n  type: [String!]!\n  issuer: IssuerInput!\n  issuanceDate: DateTime!\n  expirationDate: DateTime\n  credentialSubject: CredentialSubjectInput!\n  credentialStatus: CredentialStatusInput\n  credentialSchema: CredentialSchemaInput!\n  proof: ProofInput!\n}",
              template: "",
              reducer:
                "// Store the EIP-712 signed verifiable credential fields\nstate.context = action.input.context;\nstate.id = action.input.id;\nstate.type = action.input.type;\n\n// Store issuer\nstate.issuer = {\n  id: action.input.issuer.id,\n  ethereumAddress: action.input.issuer.ethereumAddress,\n};\n\n// Store dates\nstate.issuanceDate = action.input.issuanceDate;\nstate.expirationDate = action.input.expirationDate || null;\n\n// Store credential subject\nstate.credentialSubject = {\n  id: action.input.credentialSubject.id || null,\n  app: action.input.credentialSubject.app,\n};\n\n// Store credential status (optional)\nif (action.input.credentialStatus) {\n  state.credentialStatus = {\n    id: action.input.credentialStatus.id,\n    type: action.input.credentialStatus.type,\n  };\n} else {\n  state.credentialStatus = null;\n}\n\n// Store credential schema\nstate.credentialSchema = {\n  id: action.input.credentialSchema.id,\n  type: action.input.credentialSchema.type,\n};\n\n// Store proof (EIP-712 signature)\nstate.proof = {\n  verificationMethod: action.input.proof.verificationMethod,\n  ethereumAddress: action.input.proof.ethereumAddress,\n  created: action.input.proof.created,\n  proofPurpose: action.input.proof.proofPurpose,\n  type: action.input.proof.type,\n  proofValue: action.input.proof.proofValue,\n  eip712: {\n    domain: {\n      version: action.input.proof.eip712.domain.version,\n      chainId: action.input.proof.eip712.domain.chainId,\n    },\n    primaryType: action.input.proof.eip712.primaryType,\n  },\n};\n\n// Initialize revocation tracking\nstate.revoked = false;\nstate.revokedAt = null;\nstate.revocationReason = null;",
              errors: [
                {
                  id: "missing-context-error",
                  name: "MissingContextError",
                  code: "MISSING_CONTEXT",
                  description:
                    "The @context field is required for W3C VC compliance",
                  template: "",
                },
                {
                  id: "invalid-jwt-payload-error",
                  name: "InvalidJwtPayloadError",
                  code: "INVALID_JWT_PAYLOAD",
                  description:
                    "JWT payload does not contain valid W3C Verifiable Credential fields",
                  template: "",
                },
                {
                  id: "jwt-verification-error",
                  name: "JwtVerificationError",
                  code: "JWT_VERIFICATION_FAILED",
                  description:
                    "Failed to verify the JWT signature or the JWT is malformed",
                  template: "",
                },
                {
                  id: "invalid-claims-error",
                  name: "InvalidClaimsError",
                  code: "INVALID_CLAIMS",
                  description: "The subject claims must be valid JSON",
                  template: "",
                },
                {
                  id: "missing-type-error",
                  name: "MissingTypeError",
                  code: "MISSING_TYPE",
                  description:
                    "The type field is required and must include VerifiableCredential",
                  template: "",
                },
              ],
              examples: [],
              scope: "global",
            },
            {
              id: "8d8ed639-f7c4-43a5-8644-85729bd5b7dc",
              name: "REVOKE",
              description: "Marks the credential as revoked",
              schema:
                "input RevokeInput {\n  revokedAt: DateTime!\n  reason: String\n}",
              template: "",
              reducer:
                'if (state.revoked) {\n  throw new AlreadyRevokedError("Credential is already revoked");\n}\n\nstate.revoked = true;\nstate.revokedAt = action.input.revokedAt;\nstate.revocationReason = action.input.reason || null;',
              errors: [
                {
                  id: "already-revoked-error",
                  name: "AlreadyRevokedError",
                  code: "ALREADY_REVOKED",
                  description: "The credential is already revoked",
                  template: "",
                },
              ],
              examples: [],
              scope: "global",
            },
          ],
        },
      ],
      version: 1,
      changeLog: [],
    },
  ],
};
